import { type ReactNode } from 'react';
import { MoreHorizontal } from 'lucide-react';
import { cn } from './cn';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './DropdownMenu';

export interface RowActionItem {
  /** Stable key for React rendering. */
  key: string;
  /** Action label (already translated). */
  label: ReactNode;
  /** Click handler. Receives no arguments — bind row data at the call site. */
  onClick?: () => void;
  /** Optional icon (e.g., lucide-react). */
  icon?: ReactNode;
  /** Visual emphasis — used for destructive items (text red). */
  variant?: 'default' | 'danger';
  /** Disable the entry (hover stays but click is no-op + aria-disabled). */
  disabled?: boolean;
  /**
   * When true, render a separator before this item. Useful for splitting
   * "view/edit" from "delete".
   */
  separatorBefore?: boolean;
}

export interface RowActionsProps {
  /**
   * Optional declarative list — renders a single ⋮ trigger that opens a
   * dropdown with the given items. Use this for the unified pattern
   * (수정 / 삭제 / 복사 / 이력).
   */
  actions?: ReadonlyArray<RowActionItem>;
  /** Accessible label for the trigger (e.g., "Order ORD-1 actions"). */
  triggerLabel?: string;
  /** Legacy inline children — kept for back-compat with existing call sites. */
  children?: ReactNode;
  className?: string;
}

export const RowActions = ({ actions, triggerLabel, children, className }: RowActionsProps) => {
  if (actions && actions.length > 0) {
    return (
      <div className={cn('flex items-center justify-end', className)}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label={triggerLabel ?? '행 액션'}
              className="inline-flex h-7 w-7 items-center justify-center rounded-md border transition-colors hover:bg-[var(--surface-1)]"
              style={{
                background: 'var(--surface-0)',
                borderColor: 'var(--divider-strong)',
                color: 'var(--content-default)',
              }}
            >
              <MoreHorizontal size={14} aria-hidden="true" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" sideOffset={4} className="min-w-[160px]">
            {actions.map((item) => (
              <RowActionMenuItem key={item.key} item={item} />
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    );
  }

  return <div className={cn('flex items-center gap-1.5', className)}>{children}</div>;
};

interface RowActionMenuItemProps {
  item: RowActionItem;
}

const RowActionMenuItem = ({ item }: RowActionMenuItemProps) => {
  const dangerStyle =
    item.variant === 'danger'
      ? ({ color: 'var(--signal-danger)' } as const)
      : undefined;
  return (
    <>
      {item.separatorBefore ? <DropdownMenuSeparator /> : null}
      <DropdownMenuItem
        onSelect={() => {
          if (item.disabled) return;
          item.onClick?.();
        }}
        {...(item.disabled !== undefined ? { disabled: item.disabled } : {})}
        {...(dangerStyle ? { style: dangerStyle } : {})}
      >
        {item.icon ? <span className="mr-2 inline-flex">{item.icon}</span> : null}
        {item.label}
      </DropdownMenuItem>
    </>
  );
};

export interface RowActionButtonProps {
  onClick?: () => void;
  disabled?: boolean;
  className?: string;
  children: ReactNode;
  title?: string;
}

export const RowActionButton = ({ onClick, disabled, className, children, title }: RowActionButtonProps) => {
  const isDisabled = disabled ?? false;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={isDisabled}
      title={title}
      className={cn(
        'inline-flex h-7 items-center justify-center rounded-md border px-2.5 text-[12px] font-medium transition-colors',
        isDisabled
          ? 'opacity-40 cursor-not-allowed'
          : 'hover:bg-[var(--surface-1)] hover:text-[color:var(--content-strong)]',
        className,
      )}
      style={{
        background: 'var(--surface-0)',
        borderColor: 'var(--divider-strong)',
        color: 'var(--content-default)',
      }}
    >
      {children}
    </button>
  );
};
